import { driver } from "driver.js";
import type { Driver } from "driver.js";
import "driver.js/dist/driver.css";
import { clearBuscaRedeTourMock, isBuscaRedeTourMockMode, seedBuscaRedeTourMock } from "@/utils/buscaRedeTourMock";

export const BUSCA_REDE_TOUR_TOTAL_STEPS = 7;
/** Template do opt-in (mesmo do onboard antigo de Busca de Rede) */
export const BUSCA_REDE_TOUR_TEMPLATE = process.env.VUE_APP_AFFILIATE_SEARCH_ONBOARD;
export const BUSCA_REDE_TOUR_ONBOARD_ID = "busca-rede-tour";

/**
 * APIs usadas:
 * - Exibir?  GET  {VUE_APP_SERVER_URL}/MinhaConta/ObtemOptinPendente
 *            query: vinculo, template (VUE_APP_AFFILIATE_SEARCH_ONBOARD), sistema="PWA OMINT ONBOARD"
 *            → mostra se response.Texto existir
 * - Concluir: POST {VUE_APP_SERVER_URL}/Associado/AceitaOptin
 *            query: vinculo, template, login
 */

const SELECTORS = {
  homeShortcut: '[data-tour="busca-rede-atalho"]',
  homeSearch: '[data-tour="busca-rede-home-search"]',
  favoritos: '[data-tour="busca-rede-favoritos"]',
  subespecialidade: '[data-tour="busca-rede-subespecialidade"]',
  filtrar: '[data-tour="busca-rede-filtrar"]',
  nomeSearch: '[data-tour="busca-rede-nome-search"]',
  location: '[data-tour="busca-rede-location"]'
};

type TourVm = {
  $router: {
    push: (route: string | Record<string, unknown>) => Promise<unknown> | unknown;
    replace: (route: string | Record<string, unknown>) => Promise<unknown> | unknown;
  };
  $root: { $emit: (event: string, ...args: unknown[]) => void };
  $nextTick: (cb?: () => void) => Promise<void> | void;
  $store: {
    state: { affiliate: Record<string, unknown> };
    commit: (type: string, payload?: unknown) => void;
    dispatch: (type: string, payload?: unknown) => Promise<unknown>;
  };
  showSubspecialtyFilterModal?: boolean;
  shouldOpenOnboard?: boolean;
};

let activeDriver: Driver | null = null;
let isNavigatingBetweenPages = false;
/** Progresso só em memória (SPA). */
let tourProgress: number | null = null;
/** Cache da consulta de opt-in na sessão (evita N requests entre páginas do tour). */
let optinAllowedCache: boolean | null = null;

function delay(ms: number) {
  return new Promise(resolve => window.setTimeout(resolve, ms));
}

function getProgress(): number | null {
  return tourProgress;
}

function setProgress(step: number) {
  tourProgress = step;
}

function clearProgress() {
  tourProgress = null;
}

/**
 * Decide se o tutorial deve aparecer via API:
 * GET MinhaConta/ObtemOptinPendente → sim se `Texto` vier preenchido
 */
export async function shouldShowBuscaRedeTour(store: TourVm["$store"]): Promise<boolean> {
  if (optinAllowedCache !== null) return optinAllowedCache;

  if (!BUSCA_REDE_TOUR_TEMPLATE || BUSCA_REDE_TOUR_TEMPLATE === "0") {
    optinAllowedCache = false;
    return false;
  }

  try {
    const pendingOptin = (await store.dispatch("saude/getOnboardOptin", {
      template: BUSCA_REDE_TOUR_TEMPLATE
    })) as { Texto?: string } | undefined;

    optinAllowedCache = !!pendingOptin?.Texto;
    return optinAllowedCache;
  } catch (error) {
    console.error("Erro ao validar opt-in do tour Busca de Rede", error);
    optinAllowedCache = false;
    return false;
  }
}

export async function markBuscaRedeTourCompleted(store?: TourVm["$store"]) {
  optinAllowedCache = false;
  clearProgress();
  clearBuscaRedeTourMock(store);

  if (!store || !BUSCA_REDE_TOUR_TEMPLATE || BUSCA_REDE_TOUR_TEMPLATE === "0") return;

  try {
    await store.dispatch("saude/saveClosedOnboard", {
      onboard: BUSCA_REDE_TOUR_ONBOARD_ID,
      sendToServer: true,
      template: BUSCA_REDE_TOUR_TEMPLATE
    });
  } catch (error) {
    console.error("Erro ao gravar AceitaOptin do tour Busca de Rede", error);
  }
}

/** Útil em QA/dev para forçar nova consulta de opt-in */
export function resetBuscaRedeTour() {
  optinAllowedCache = null;
  clearProgress();
  clearBuscaRedeTourMock();
  if (activeDriver) {
    const current = activeDriver;
    activeDriver = null;
    current.destroy();
  }
}

export function shouldAutoSelectSpecialtyForTour(): boolean {
  return false;
}

export function isBuscaRedeTourActive(): boolean {
  return getProgress() !== null;
}

async function destroyDriver(persist: boolean, store?: TourVm["$store"]) {
  if (persist) {
    await markBuscaRedeTourCompleted(store);
  } else {
    clearProgress();
    clearBuscaRedeTourMock(store);
  }

  if (activeDriver) {
    const current = activeDriver;
    activeDriver = null;
    current.destroy();
  }
}

async function finishTourAndGoHome(vm: TourVm) {
  await destroyDriver(true, vm.$store);
  // Full reload limpa estado mock do Vuex e reabre a home da rede
  globalThis.window.location.assign("/redecredenciada-home");
}

function waitForElement(selector: string, timeoutMs = 8000): Promise<Element | null> {
  return new Promise(resolve => {
    const existing = document.querySelector(selector);
    if (existing) {
      resolve(existing);
      return;
    }

    const startedAt = Date.now();
    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        observer.disconnect();
        resolve(el);
      } else if (Date.now() - startedAt > timeoutMs) {
        observer.disconnect();
        resolve(null);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    window.setTimeout(() => {
      observer.disconnect();
      resolve(document.querySelector(selector));
    }, timeoutMs);
  });
}

function createBaseConfig(store?: TourVm["$store"]) {
  return {
    animate: true,
    overlayColor: "#4E4E4E",
    overlayOpacity: 0.55,
    smoothScroll: true,
    allowClose: true,
    stagePadding: 8,
    stageRadius: 8,
    allowKeyboardControl: true,
    disableActiveInteraction: true,
    popoverClass: "busca-rede-tour-theme",
    showButtons: ["next", "close"] as Array<"next" | "close" | "previous">,
    showProgress: true,
    nextBtnText: "Próximo",
    doneBtnText: "Próximo",
    progressText: "{{current}} de {{total}}",
    onCloseClick: () => {
      destroyDriver(false, store);
    },
    onDestroyStarted: () => {
      if (isNavigatingBetweenPages) {
        isNavigatingBetweenPages = false;
        if (activeDriver) {
          const current = activeDriver;
          activeDriver = null;
          current.destroy();
        }
        return;
      }
      destroyDriver(false, store);
    }
  };
}

async function navigateToDoctorsForTour(vm: TourVm) {
  setProgress(3);
  seedBuscaRedeTourMock(vm.$store);
  isNavigatingBetweenPages = true;
  await vm.$router.push({ name: "affiliate.docs" });
}

export async function maybeStartBuscaRedeTourFromHome(vm: TourVm) {
  if (vm.shouldOpenOnboard) return;
  if (!(await shouldShowBuscaRedeTour(vm.$store))) return;

  const progress = getProgress();
  if (progress !== null && progress > 0) return;

  const shortcut = await waitForElement(SELECTORS.homeShortcut, 4000);
  if (!shortcut) {
    return;
  }

  setProgress(0);
  startHomeTour(vm);
}

function startHomeTour(vm: TourVm) {
  if (activeDriver) {
    const current = activeDriver;
    activeDriver = null;
    current.destroy();
  }

  activeDriver = driver({
    ...createBaseConfig(vm.$store),
    steps: [
      {
        element: SELECTORS.homeShortcut,
        popover: {
          title: "Uma nova busca de rede para você",
          description: "Conheça a nova busca de rede credenciada, mais rápida e intuitiva.",
          side: "top",
          align: "center",
          progressText: "1 de 7",
          nextBtnText: "Próximo",
          doneBtnText: "Próximo",
          onNextClick: async () => {
            setProgress(1);
            isNavigatingBetweenPages = true;
            if (activeDriver) {
              const current = activeDriver;
              activeDriver = null;
              current.destroy();
            }
            await vm.$router.push("/redecredenciada-home");
          }
        }
      }
    ]
  });

  activeDriver.drive(0);
}

export async function maybeResumeBuscaRedeTourOnAffiliateHome(vm: TourVm) {
  if (!(await shouldShowBuscaRedeTour(vm.$store))) return;

  let progress = getProgress();

  if (progress === null || progress === 0) {
    setProgress(1);
    progress = 1;
  }

  if (progress >= 3) {
    setProgress(1);
    progress = 1;
  }

  if (progress !== 1 && progress !== 2) return;

  const searchEl = await waitForElement(SELECTORS.homeSearch, 8000);
  if (!searchEl) return;

  startAffiliateHomeTour(vm, progress === 2 ? 1 : 0);
}

function startAffiliateHomeTour(vm: TourVm, startIndex = 0) {
  if (activeDriver) {
    const current = activeDriver;
    activeDriver = null;
    current.destroy();
  }

  activeDriver = driver({
    ...createBaseConfig(vm.$store),
    steps: [
      {
        element: SELECTORS.homeSearch,
        popover: {
          title: "Busque pela especialidade ou nome do médico",
          description:
            "Utilize o campo de busca para encontrar profissionais por especialidade médica ou pelo nome do prestador.",
          side: "bottom",
          align: "center",
          progressText: "2 de 7",
          nextBtnText: "Próximo",
          doneBtnText: "Próximo",
          onNextClick: async () => {
            const favoritos = document.querySelector(SELECTORS.favoritos);
            if (!favoritos) {
              if (activeDriver) {
                const current = activeDriver;
                activeDriver = null;
                current.destroy();
              }
              await navigateToDoctorsForTour(vm);
              return;
            }

            setProgress(2);
            activeDriver?.moveNext();
          }
        }
      },
      {
        element: SELECTORS.favoritos,
        popover: {
          title: "Acesse seus favoritos",
          description:
            "Marque seus prestadores mais importantes para reuni-los nesta área e acessá-los rapidamente sempre que precisar.",
          side: "bottom",
          align: "start",
          progressText: "3 de 7",
          nextBtnText: "Próximo",
          doneBtnText: "Próximo",
          onNextClick: async () => {
            if (activeDriver) {
              const current = activeDriver;
              activeDriver = null;
              current.destroy();
            }
            await navigateToDoctorsForTour(vm);
          }
        }
      }
    ]
  });

  activeDriver.drive(startIndex);
}

export async function maybeResumeBuscaRedeTourOnDoctorsList(vm: TourVm) {
  if (!(await shouldShowBuscaRedeTour(vm.$store))) return;

  const progress = getProgress();
  if (progress !== 3 && progress !== 4 && progress !== 5 && progress !== 6) return;

  if (progress === 3) {
    vm.showSubspecialtyFilterModal = true;
    await vm.$nextTick?.();
    await waitForElement(SELECTORS.subespecialidade, 8000);
    await delay(500);

    const target = document.querySelector(SELECTORS.subespecialidade) as HTMLElement | null;
    if (target) {
      const modalBody = target.closest(".sticked-modal-body") as HTMLElement | null;
      if (modalBody) modalBody.scrollTop = 0;
      target.scrollIntoView({ block: "center", inline: "nearest", behavior: "auto" });
      await delay(150);
    }
  }

  const startIndex = Math.max(0, progress - 3);
  const targetSelector = progress === 3 ? SELECTORS.subespecialidade : SELECTORS.filtrar;
  await waitForElement(targetSelector, 8000);

  startDoctorsListTour(vm, startIndex);
}

function startDoctorsListTour(vm: TourVm, startIndex = 0) {
  if (activeDriver) {
    const current = activeDriver;
    activeDriver = null;
    current.destroy();
  }

  const allSteps = [
    {
      element: SELECTORS.subespecialidade,
      popover: {
        title: "Selecione a subespecialidade",
        description: "Escolha a área de atuação específica para encontrar os profissionais ideais.",
        side: "top" as const,
        align: "center" as const,
        progressText: "4 de 7",
        nextBtnText: "Próximo",
        doneBtnText: "Próximo",
        onNextClick: () => {
          vm.showSubspecialtyFilterModal = false;
          setProgress(4);
          window.setTimeout(() => {
            activeDriver?.moveNext();
            window.setTimeout(() => activeDriver?.refresh(), 100);
          }, 300);
        }
      },
      onHighlighted: () => {
        const target = document.querySelector(SELECTORS.subespecialidade) as HTMLElement | null;
        const modalBody = target?.closest(".sticked-modal-body") as HTMLElement | null;
        if (modalBody) modalBody.scrollTop = 0;
        window.setTimeout(() => activeDriver?.refresh(), 80);
      }
    },
    {
      element: SELECTORS.filtrar,
      popover: {
        title: "Filtre os resultados",
        description:
          "Refine sua busca utilizando filtros por subespecialidades, agendamento online, WhatsApp do local, facilidades e idiomas para encontrar o prestador ideal.",
        side: "bottom" as const,
        align: "start" as const,
        progressText: "5 de 7",
        nextBtnText: "Próximo",
        doneBtnText: "Próximo",
        onNextClick: () => {
          setProgress(5);
          activeDriver?.moveNext();
        }
      }
    },
    {
      element: SELECTORS.nomeSearch,
      popover: {
        title: "Localize dentro dos resultados",
        description: "Digite um nome ou parte do nome para localizar rapidamente o prestador que você deseja.",
        side: "bottom" as const,
        align: "center" as const,
        progressText: "6 de 7",
        nextBtnText: "Próximo",
        doneBtnText: "Próximo",
        onNextClick: () => {
          setProgress(6);
          activeDriver?.moveNext();
        }
      }
    },
    {
      element: SELECTORS.location,
      popover: {
        title: "Selecione sua localização",
        description:
          "O resultado é apresentado a partir de um ponto de referência padrão. Você pode alterar sua localização para encontrar prestadores próximos a sua casa, seu trabalho ou em outros estados e cidades. Após alterar a localização, aplique os filtros novamente.",
        side: "bottom" as const,
        align: "center" as const,
        progressText: "7 de 7",
        doneBtnText: "Concluir",
        nextBtnText: "Concluir",
        onNextClick: () => {
          finishTourAndGoHome(vm);
        }
      }
    }
  ];

  const resolvedSteps = allSteps.filter(step => {
    if (step.element === SELECTORS.subespecialidade || step.element === SELECTORS.location) {
      return !!document.querySelector(step.element);
    }
    return true;
  });

  if (!resolvedSteps.length) {
    clearProgress();
    clearBuscaRedeTourMock(vm.$store);
    return;
  }

  activeDriver = driver({
    ...createBaseConfig(vm.$store),
    smoothScroll: startIndex !== 0,
    steps: resolvedSteps
  });

  const safeIndex = Math.min(startIndex, resolvedSteps.length - 1);
  activeDriver.drive(safeIndex);
}

export function stopBuscaRedeTour(persist = false, store?: TourVm["$store"]) {
  isNavigatingBetweenPages = false;
  destroyDriver(persist, store);
}

export { isBuscaRedeTourMockMode };

if (typeof window !== "undefined") {
  (window as Window & { resetBuscaRedeTour?: () => void }).resetBuscaRedeTour = resetBuscaRedeTour;
}
